// hooks/useInfinitePayments.ts
import { useInfiniteQuery, InfiniteData } from '@tanstack/react-query';
import { SupabaseQueryBuilder } from '../lib/supabaseQueryBuilder.ts';
import { AccountingStatus, Payment } from '../types';
import { supabase } from '../lib/supabaseClient';
import {
    normalizeMissionAssignmentAccountingStatus,
    normalizeMissionStatusToAccounting,
} from '@/utils/missionAccounting';
import {
    MATCH_ACCOUNTING_LEAGUE_COLUMNS,
    applyMatchAssignmentLeagueScope,
    mapMatchAssignmentToPayment,
    withAccountingLeagueScope,
} from '@/lib/payments/matchAssignmentPayment';
import type { LeagueLevelRef } from '@/lib/leagueScope/matchScope';

const PAGE_SIZE = 50;

const resolveMissionPaymentAccountingStatus = (
    assignment: { accounting_status?: string | null; status?: string | null },
    mission?: { mission_status?: string | null }
): AccountingStatus => {
    const accountingStatus = normalizeMissionAssignmentAccountingStatus(
        assignment.accounting_status
    );
    if (accountingStatus !== AccountingStatus.NOT_ENTERED) {
        return accountingStatus;
    }

    const workflowStatus = normalizeMissionAssignmentAccountingStatus(
        assignment.status
    );
    if (workflowStatus !== AccountingStatus.NOT_ENTERED) {
        return workflowStatus;
    }

    return normalizeMissionStatusToAccounting(mission?.mission_status);
};

export interface InfinitePaymentsOptions {
    matchAccountingStatus?: AccountingStatus | AccountingStatus[];
    excludePaid?: boolean;
    enabled?: boolean;
    pageSize?: number;
    filters?: Record<string, any>;
    includeMissions?: boolean;
    search?: string;
    season?: string | null;
    /**
     * All leagues (useLeagues) — levels + the EXT_COMPETITIONS league. With a
     * `filters.leagueId`, a regional / wilaya league then also gets the external
     * matches it organises (accounting league rule, lib/leagueScope/matchScope.ts).
     * Without it, only the league's own groups are read.
     */
    accountingLeagues?: readonly (LeagueLevelRef & { code?: string | null; name?: string | null })[] | null;
}

interface InfinitePaymentsPage {
    data: Payment[];
    page: number;
    hasMore: boolean;
}

function mapMissionAssignmentToPayment(ma: any): Payment | null {
    if (ma.is_absent) return null;

    const official = ma.official;
    const mission = ma.mission;
    const travelKm = Number(ma.travel_distance_km ?? 0);
    const distanceBonusValue = ma.distance_bonus !== null ? Number(ma.distance_bonus) : (travelKm >= 1000 ? 10000 : 0);
    const indemnity = Number(ma.payment_amount ?? 0) + distanceBonusValue;

    const rawMissionIrg = ma.irg_amount !== null ? Number(ma.irg_amount) : 0;
    const missionStatus = (ma.accounting_status as AccountingStatus) || AccountingStatus.NOT_ENTERED;
    const isMissionFinalized = missionStatus === AccountingStatus.VALIDATED || missionStatus === AccountingStatus.CLOSED;

    const irgAmount = (isMissionFinalized || rawMissionIrg !== 0) ? (ma.irg_amount !== null ? Number(ma.irg_amount) : null) : null;

    const rawTotal = ma.net_amount !== null ? Number(ma.net_amount) : indemnity;
    const total = isMissionFinalized ? rawTotal : 0;

    const accountingStatus = resolveMissionPaymentAccountingStatus(ma, mission);
    const leagueId = ma.league_id || mission?.league_id || '';

    return {
        id: ma.id,
        officialId: ma.official_id,
        matchId: undefined,
        missionId: ma.mission_id,
        assignmentType: 'mission',
        leagueId,
        groupId: undefined,
        matchDescription: mission?.title || `Mission ${mission?.code || ''}`,
        matchDate: mission?.start_at ? new Date(mission.start_at).toISOString().split('T')[0] : '',
        officialName: official?.full_name || [official?.first_name, official?.last_name].filter(Boolean).join(' ') || '',
        officialNameAr: [official?.first_name_ar, official?.last_name_ar].filter(Boolean).join(' ') || null,
        officialCategory: official?.category ?? null,
        bankAccountNumber: official?.bank_account_number || null,
        role: ma.role_label || 'Auditeur',
        indemnity,
        baseIndemnity: Number(ma.payment_amount ?? 0),
        travelDistanceInKm: travelKm,
        distanceBonus: distanceBonusValue,
        irgAmount,
        total,
        accountingStatus,
        notes: ma.notes || null,
        originalOfficialId: null,
        validatedByUserId: ma.validated_by || null,
        validatedAt: ma.validated_at || null,
        validatedByName: undefined,
        rejectionReason: ma.rejection_reason ?? null,
        rejectionComment: ma.rejection_comment ?? null,
        isPaid: ma.is_paid || false,
        paidAt: ma.paid_at || null,
        paymentBatchId: ma.payment_batch_id || null,
        paymentBatchReference: ma.paymentBatchInfo?.payment_batches?.reference || null,
        paymentMonthOverride: ma.payment_month_override || null,
    } as Payment;
}

/**
 * Chargeur d'une page de paiements (désignations de match + missions en page 1),
 * partagé par useInfinitePayments et fetchAllPayments : l'export Finances lit
 * exactement la même requête — mêmes filtres serveur, même RLS — que le tableau.
 */
export function createPaymentsPageFetcher(
    options: InfinitePaymentsOptions = {},
    // strict : une erreur lève au lieu de rendre une page vide (un export ne
    // doit jamais être silencieusement tronqué).
    // stableOrder : tri par id pour la pagination range() — sans ORDER BY,
    // Postgres peut rendre une ligne sur deux pages et en sauter une autre.
    // (La recherche RPC trie déjà par match_date, id.)
    { strict = false, stableOrder = false }: { strict?: boolean; stableOrder?: boolean } = {},
) {
    const {
        matchAccountingStatus,
        excludePaid,
        pageSize: requestedPageSize = PAGE_SIZE,
        filters: rawFilters = {},
        includeMissions = true,
        search,
        season,
        accountingLeagues,
    } = options;

    const pageSize = requestedPageSize;

    // Build filters for the query builder
    const queryFilters: Record<string, any> = { ...rawFilters };
    if (matchAccountingStatus) queryFilters.matchAccountingStatus = matchAccountingStatus;
    if (excludePaid) queryFilters.isPaid = false;
    if (season) queryFilters.matchSeason = season;
    // Match-assignment filters with the accounting league scope (missions keep
    // reading rawFilters.leagueId).
    const matchQueryFilters = withAccountingLeagueScope(queryFilters, accountingLeagues);

    // League filter (filters.leagueId → matches.league_groups.league_id): the match
    // and league_groups embeds must both be !inner, otherwise PostgREST only nulls
    // the embed and still returns every league's assignments.
    const hasLeagueFilter = Array.isArray(rawFilters?.leagueId)
        ? rawFilters.leagueId.filter(Boolean).length > 0
        : Boolean(rawFilters?.leagueId);

    // When matchAccountingStatus is set, use !inner join so PostgREST excludes non-matching rows
    const needsInnerJoin = !!matchAccountingStatus || hasLeagueFilter;
    const matchJoin = needsInnerJoin ? 'match:matches!inner(' : 'match:matches(';
    const leagueGroupJoin = hasLeagueFilter
        ? 'leagueGroup:league_groups!matches_league_group_id_fkey!inner('
        : 'leagueGroup:league_groups!matches_league_group_id_fkey(';

    const slimSelect = `
        id,
        role,
        official_id,
        original_official_id,
        indemnity_amount,
        travel_distance_km,
        notes,
        match_id,
        is_paid,
        paid_at,
        payment_batch_id,
        is_not_assigned,
        is_absent,
        payment_month_override,
        base_indemnity,
        irg_amount,
        net_amount,
        distance_bonus,
        ${matchJoin}
            id,
            match_date,
            game_day,
            cup_round,
            league_group_id,
            accounting_status,
            rejection_reason,
            rejection_comment,
            validated_by,
            validated_at,${MATCH_ACCOUNTING_LEAGUE_COLUMNS},
            ${leagueGroupJoin}
                id,
                league_id,
                kind,
                league:leagues!league_groups_league_id_fkey(id,name)
            ),
            homeTeam:teams!matches_home_team_id_fkey(id,name),
            awayTeam:teams!matches_away_team_id_fkey(id,name),
            validatedBy:profiles!matches_validated_by_fkey(full_name)
        ),
        official:officials!match_assignments_official_id_fkey(id,first_name,last_name,first_name_ar,last_name_ar,full_name,bank_account_number,category),
        paymentBatchInfo:accounting_periods!match_assignments_payment_batch_id_fkey(payment_batches!payment_batches_id_fkey(reference))
    `;

    return async (pageParam: number): Promise<InfinitePaymentsPage> => {
        let matchAssignments: any[] = [];
        let currentPage = pageParam;
        let hasMore = false;

        if (search) {
            // Server-side search via RPC — returns matching assignment IDs, paginated
            const offset = (pageParam - 1) * pageSize;
            const accountingStatuses = matchAccountingStatus
                ? (Array.isArray(matchAccountingStatus) ? matchAccountingStatus : [matchAccountingStatus])
                : null;

            // Ligue du filtre (une seule) : la recherche ne remonte que ses
            // désignations. Plusieurs ligues ou aucune : pas de filtre serveur.
            const searchLeagueFilter = rawFilters?.leagueId;
            const searchLeagueId = Array.isArray(searchLeagueFilter)
                ? (searchLeagueFilter.length === 1 ? searchLeagueFilter[0] : null)
                : (searchLeagueFilter || null);

            const { data: searchResults, error: searchError } = await supabase
                .rpc('search_match_assignment_ids', {
                    p_search: search,
                    p_accounting_statuses: accountingStatuses,
                    p_exclude_paid: excludePaid || false,
                    p_limit: pageSize,
                    p_offset: offset,
                    p_league_id: searchLeagueId,
                });

            if (searchError) {
                if (strict) throw new Error(searchError.message);
                console.error("useInfinitePayments: search RPC failed", searchError.message);
                return { data: [], page: pageParam, hasMore: false };
            }

            if (!searchResults || searchResults.length === 0) {
                // No match assignments found — still check missions on page 1
                matchAssignments = [];
                currentPage = pageParam;
                hasMore = false;
            } else {
                const ids = searchResults.map((r: any) => r.assignment_id);
                const totalCount = Number(searchResults[0]?.total_count || 0);

                // Fetch full data for the matching IDs (season filter requires the
                // matches!inner join, which is active when matchAccountingStatus is set)
                let byIdQuery = supabase
                    .from('match_assignments')
                    .select(slimSelect)
                    .in('id', ids);
                if (season) {
                    byIdQuery = byIdQuery.eq('matches.season', season);
                }
                byIdQuery = applyMatchAssignmentLeagueScope(byIdQuery, matchQueryFilters);
                const { data, error } = await byIdQuery;

                if (error) {
                    if (strict) throw new Error(error.message);
                    console.error("useInfinitePayments: fetch by IDs failed", error.message);
                    return { data: [], page: pageParam, hasMore: false };
                }

                matchAssignments = data || [];
                hasMore = pageParam * pageSize < totalCount;
            }
        } else {
            // Normal paginated query (no search)
            const assignmentsResp = await SupabaseQueryBuilder.executePaginatedQuery<any>(
                'match_assignments',
                slimSelect,
                {
                    pagination: {
                        page: pageParam,
                        pageSize,
                        ...(stableOrder ? { sortBy: 'id', sortOrder: 'asc' as const } : {}),
                    },
                    filters: matchQueryFilters,
                }
            );
            matchAssignments = assignmentsResp.data || [];
            currentPage = assignmentsResp.page;
            hasMore = assignmentsResp.hasMore;
        }

        const matchPayments: Payment[] = matchAssignments
            .map(mapMatchAssignmentToPayment)
            .filter((p): p is Payment => p !== null);

        let allPayments = matchPayments;

        // Fetch ALL missions on page 1 only (missions are few ~20-50)
        if (includeMissions && pageParam === 1) {
            const missionSelect = `
                id,
                mission_id,
                official_id,
                role_label,
                status,
                payment_amount,
                travel_distance_km,
                accounting_status,
                is_absent,
                notes,
                validated_by,
                validated_at,
                rejection_reason,
                rejection_comment,
                league_id,
                is_paid,
                paid_at,
                payment_batch_id,
                payment_month_override,
                irg_amount,
                net_amount,
                distance_bonus,
                official:officials!mission_assignments_official_id_fkey(
                    id, first_name, last_name, first_name_ar, last_name_ar, full_name, bank_account_number, category
                ),
                mission:missions${season ? '!inner' : ''}!mission_assignments_mission_id_fkey(
                    id, title, code, target_type, target_name, start_at, league_id, mission_status
                ),
                paymentBatchInfo:accounting_periods!mission_assignments_payment_batch_id_fkey(payment_batches!payment_batches_id_fkey(reference))
            `;

            let missionQuery = supabase
                .from('mission_assignments')
                .select(missionSelect)
                .not('official_id', 'is', null)
                // Miroir exact de usePayments : une affectation annulée (audit de stade
                // annulé puis recréé, officiel retiré par une correction d'équipe) n'est
                // pas une indemnité à saisir et n'a rien à faire sur le kanban. La moitié
                // `is_paid.eq.true` est volontaire : elle garde visible l'historique déjà
                // payé, qu'un simple .neq('status','cancelled') ferait disparaître.
                .or('status.neq.cancelled,is_paid.eq.true');

            if (excludePaid) {
                missionQuery = missionQuery.eq('is_paid', false);
            }

            // Apply league filter if provided
            const leagueIdFilter = rawFilters?.leagueId;
            if (leagueIdFilter) {
                if (Array.isArray(leagueIdFilter)) {
                    missionQuery = missionQuery.in('league_id', leagueIdFilter);
                } else {
                    missionQuery = missionQuery.eq('league_id', leagueIdFilter);
                }
            }

            // Season filter via the embedded missions resource
            if (season) {
                missionQuery = missionQuery.eq('missions.season', season);
            }

            const { data: missionData, error } = await missionQuery;

            if (error) {
                if (strict) throw new Error(error.message);
                console.error("useInfinitePayments: mission query failed", error.message);
            }

            if (!error && missionData) {
                let missionPayments: Payment[] = missionData
                    .map(mapMissionAssignmentToPayment)
                    .filter((p): p is Payment => p !== null);

                // Filter missions by accounting status to match the server-side filter
                // applied to match assignments (missions resolve status client-side)
                if (matchAccountingStatus) {
                    const statuses = Array.isArray(matchAccountingStatus)
                        ? matchAccountingStatus
                        : [matchAccountingStatus];
                    missionPayments = missionPayments.filter((p) =>
                        statuses.includes(p.accountingStatus)
                    );
                }

                // Client-side league filter for missions
                if (leagueIdFilter) {
                    const matchLeague = (leagueId: string) => {
                        if (!leagueId) return false;
                        if (Array.isArray(leagueIdFilter)) {
                            return leagueIdFilter.includes(leagueId);
                        }
                        return leagueId === leagueIdFilter;
                    };
                    missionPayments = missionPayments.filter((p) => matchLeague(p.leagueId));
                }

                // When searching, also filter missions by search term (client-side, token-based)
                if (search) {
                    const tokens = search.toLowerCase().split(' ').filter(Boolean);
                    missionPayments = missionPayments.filter((p) => {
                        const searchable = `${p.officialName} ${p.matchDescription}`.toLowerCase();
                        return tokens.every(token => searchable.includes(token));
                    });
                }

                allPayments = [...matchPayments, ...missionPayments];
            }
        }

        return {
            data: allPayments,
            page: currentPage,
            hasMore,
        };
    };
}

/**
 * Toutes les pages de la requête (export) : tri stable par id, erreurs levées,
 * dédoublonnage par id (une ligne déplacée entre deux pages pendant la lecture
 * n'apparaît qu'une fois) ; garde-fou de `maxPages` pages.
 */
export async function fetchAllPayments(
    options: InfinitePaymentsOptions = {},
    { maxPages = 400 }: { maxPages?: number } = {},
): Promise<Payment[]> {
    const fetchPage = createPaymentsPageFetcher(options, { strict: true, stableOrder: true });
    const byId = new Map<string, Payment>();
    let page = 1;
    for (let i = 0; i < maxPages; i += 1) {
        const result = await fetchPage(page);
        for (const payment of result.data) {
            // Les id de désignation de match et de mission sont des uuid distincts.
            const key = `${payment.assignmentType ?? 'match'}:${payment.id}`;
            if (!byId.has(key)) byId.set(key, payment);
        }
        if (!result.hasMore) return [...byId.values()];
        page = result.page + 1;
    }
    throw new Error(`Export interrompu : plus de ${maxPages} pages de paiements.`);
}

/** Racine de clé de useInfinitePayments (liste Finances) ; invalidée par préfixe par le temps réel (lib/realtime/globalRealtimeHandlers.ts). */
export const INFINITE_PAYMENTS_QUERY_KEY_PREFIX = 'infinite-payments';

export function useInfinitePayments(options: InfinitePaymentsOptions = {}) {
    const {
        matchAccountingStatus,
        excludePaid,
        enabled = true,
        pageSize = PAGE_SIZE,
        filters: rawFilters = {},
        includeMissions = true,
        search,
        season,
        accountingLeagues,
    } = options;
    const fetchPage = createPaymentsPageFetcher(options);
    // The league list changes the server filter (externals of a regional league):
    // its effect is part of the key, not the list itself.
    const accountingScope = withAccountingLeagueScope({ leagueId: rawFilters?.leagueId }, accountingLeagues);

    return useInfiniteQuery<
        InfinitePaymentsPage,
        Error,
        InfiniteData<InfinitePaymentsPage>,
        (string | Record<string, any>)[],
        number
    >({
        queryKey: [INFINITE_PAYMENTS_QUERY_KEY_PREFIX, { matchAccountingStatus, excludePaid, includeMissions, filters: rawFilters, pageSize, search, season: season ?? null, accountingScope }],
        queryFn: ({ pageParam }) => fetchPage(pageParam),
        initialPageParam: 1,
        getNextPageParam: (lastPage) => {
            return lastPage.hasMore ? lastPage.page + 1 : undefined;
        },
        enabled,
        staleTime: 30_000,
    });
}
